import { auth } from "@/lib/auth/client";
import {
  captureSessionTokenFromData,
  captureSessionTokenFromResponse,
  clearBearerToken,
} from "@/lib/auth/token-store";
import { create, type StateCreator } from "zustand";
import { getErrorMessage, redirectToDashboard, redirectToLogin } from "./auth.store.helpers";

type AuthState = {
  login: {
    email: string;
    password: string;
    isLoading: boolean;
    error: string | null;
    setEmail: (email: string) => void;
    setPassword: (password: string) => void;
    submit: () => Promise<void>;
    signOut: () => Promise<void>;
  };
  signup: {
    username?: string;
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    isLoading: boolean;
    error: string | null;
    setName: (name: string) => void;
    setEmail: (email: string) => void;
    setPassword: (password: string) => void;
    setUsername: (username: string) => void;
    setConfirmPassword: (confirmPassword: string) => void;
    submit: () => Promise<void>;
  };
  oauth: {
    isLoading: { google: boolean; github: boolean };
    error: string | null;
    inititateGoogleSignIn: () => Promise<void>;
    initiateGitHubSignIn: () => Promise<void>;
    clearError: () => void;
  };
  dashboard: {
    isSigningOut: boolean;
    error: string | null;
    signOut: () => Promise<void>;
  };
};

type LoginSlice = { login: AuthState["login"] };
type SignupSlice = { signup: AuthState["signup"] };
type OAuthSlice = { oauth: AuthState["oauth"] };
type DashboardSlice = { dashboard: AuthState["dashboard"] };

const createLoginSlice: StateCreator<AuthState, [], [], LoginSlice> = (set, get) => ({
  login: {
    email: "",
    password: "",
    isLoading: false,
    error: null,
    setEmail: (email) => set((state) => ({ login: { ...state.login, email } })),
    setPassword: (password) => set((state) => ({ login: { ...state.login, password } })),
    submit: async () => {
      const { email, password, isLoading } = get().login;
      if (isLoading) return;

      set((state) => ({
        login: { ...state.login, error: null, isLoading: true },
      }));

      const result = await auth.signIn.email({
        email,
        password,
        rememberMe: true,
        fetchOptions: {
          onSuccess: (context) => {
            // Header capture is a fallback: browsers hide `set-auth-token`
            // unless the API exposes it, so the JSON body (always readable)
            // is the primary source.
            captureSessionTokenFromResponse(context.response);
            captureSessionTokenFromData(context.data);
          },
        },
      });

      if (result.error) {
        set((state) => ({
          login: {
            ...state.login,
            error: getErrorMessage(result.error, "Failed to sign in with email and password"),
            isLoading: false,
          },
        }));
        return;
      }

      captureSessionTokenFromData(result.data);

      set((state) => ({
        login: {
          ...state.login,
          isLoading: false,
        },
      }));

      // Full-page navigation (not router state): the dashboard boots with the
      // freshly persisted bearer token, so session resolution works even when
      // third-party cookies are blocked. AuthGuard remains as a fallback for
      // same-site browsers where the session atom is already warm.
      redirectToDashboard();
    },
    signOut: async () => {
      try {
        await auth.signOut();
        clearBearerToken();
        redirectToLogin();
      } catch (error) {
        set((state) => ({
          login: {
            ...state.login,
            error: getErrorMessage(error, "Failed to sign out"),
          },
        }));
        console.error("Error during sign out:", error);
      }
    },
  },
});

const createSignupSlice: StateCreator<AuthState, [], [], SignupSlice> = (set, get) => ({
  signup: {
    name: "",
    email: "",
    password: "",
    username: "",
    confirmPassword: "",
    isLoading: false,
    error: null,
    setName: (name) => set((state) => ({ signup: { ...state.signup, name } })),
    setUsername: (username) => set((state) => ({ signup: { ...state.signup, username } })),
    setEmail: (email) => set((state) => ({ signup: { ...state.signup, email } })),
    setPassword: (password) => set((state) => ({ signup: { ...state.signup, password } })),
    setConfirmPassword: (confirmPassword) =>
      set((state) => ({ signup: { ...state.signup, confirmPassword } })),
    submit: async () => {
      const { name, email, password, confirmPassword, isLoading, username } = get().signup;
      if (isLoading) return;

      if (password.length < 8) {
        set((state) => ({
          signup: {
            ...state.signup,
            error: "Password must be at least 8 characters long",
          },
        }));
        return;
      }

      if (password !== confirmPassword) {
        set((state) => ({
          signup: { ...state.signup, error: "Passwords do not match" },
        }));
        return;
      }

      set((state) => ({
        signup: { ...state.signup, error: null, isLoading: true },
      }));

      try {
        const result = await auth.signUp.email({
          name,
          email,
          password,
          username: username?.trim() ? username.trim() : email.split("@")[0], // Assuming username is the same as email
          callbackURL: `${window.location.origin}/login`,
          fetchOptions: {
            onSuccess: (context) => {
              captureSessionTokenFromResponse(context.response);
              captureSessionTokenFromData(context.data);
            },
          },
        });

        if (result.error) {
          set((state) => ({
            signup: {
              ...state.signup,
              error: getErrorMessage(result.error, "Unable to create account"),
              isLoading: false,
            },
          }));
          return;
        }

        captureSessionTokenFromData(result.data);

        redirectToLogin();
      } catch (submitError) {
        set((state) => ({
          signup: {
            ...state.signup,
            error: getErrorMessage(submitError, "Unable to create account"),
          },
        }));
      } finally {
        set((state) => ({
          signup: { ...state.signup, isLoading: false },
        }));
      }
    },
  },
});

async function startOauthSignIn(provider: "github" | "google"): Promise<{ error: unknown }> {
  const response = await auth.signIn.social({
    provider,
    callbackURL: `${window.location.origin}/login`,
    errorCallbackURL: `${window.location.origin}/login?oauth_error=${provider}`,
  });

  if (response.error) return response;

  // Default fetch plugins are disabled (see the auth client), so the
  // provider URL returned by the API must be opened explicitly. Without this
  // the `social` request resolves with `{ redirect: true, url }` and the UI
  // spins forever without ever leaving for the OAuth provider.
  const url = (response.data as { url?: unknown } | null)?.url;
  if (typeof url === "string" && url.startsWith("https://")) {
    window.location.href = url;
    return response;
  }

  return { error: new Error(`Failed to initiate ${provider} sign-in`) };
}

const createOAuthSlice: StateCreator<AuthState, [], [], OAuthSlice> = (set) => ({
  oauth: {
    isLoading: { google: false, github: false },
    error: null,
    inititateGoogleSignIn: async () => {
      set((state) => ({
        oauth: {
          ...state.oauth,
          isLoading: { ...state.oauth.isLoading, google: true },
          error: null,
        },
      }));

      const response = await startOauthSignIn("google");

      if (response.error) {
        set((state) => ({
          oauth: {
            ...state.oauth,
            isLoading: { ...state.oauth.isLoading, google: false },
            error: getErrorMessage(response.error, "Failed to initiate Google sign-in"),
          },
        }));
      }
    },
    initiateGitHubSignIn: async () => {
      set((state) => ({
        oauth: {
          ...state.oauth,
          isLoading: { ...state.oauth.isLoading, github: true },
          error: null,
        },
      }));

      const response = await startOauthSignIn("github");

      if (response.error) {
        set((state) => ({
          oauth: {
            ...state.oauth,
            isLoading: { ...state.oauth.isLoading, github: false },
            error: getErrorMessage(response.error, "Failed to initiate GitHub sign-in"),
          },
        }));
      }
    },
    clearError: () => set((state) => ({ oauth: { ...state.oauth, error: null } })),
  },
});

const createDashboardSlice: StateCreator<AuthState, [], [], DashboardSlice> = (set, get) => ({
  dashboard: {
    isSigningOut: false,
    error: null,
    signOut: async () => {
      const { isSigningOut } = get().dashboard;
      if (isSigningOut) return;

      set((state) => ({
        dashboard: { ...state.dashboard, error: null, isSigningOut: true },
      }));

      try {
        await auth.signOut();
        clearBearerToken();
        redirectToLogin();
      } catch (signOutError) {
        set((state) => ({
          dashboard: {
            ...state.dashboard,
            error: getErrorMessage(signOutError, "Could not sign out"),
            isSigningOut: false,
          },
        }));
      }
    },
  },
});

export const useAuthStore = create<AuthState>()((...a) => ({
  ...createLoginSlice(...a),
  ...createSignupSlice(...a),
  ...createOAuthSlice(...a),
  ...createDashboardSlice(...a),
}));
